import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Megaphone } from 'lucide-react';
import { Button, Card, CardContent } from '@/components/ui';
import { useOrgMemberStore } from '@/store/orgMember';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { memberPortalService } from '@/services/memberPortal';
import { cn } from '@/lib/utils';

interface OrgConsumerNoticesSectionProps {
  hasClubMemberAccess: boolean;
}

export function OrgConsumerNoticesSection({ hasClubMemberAccess }: OrgConsumerNoticesSectionProps) {
  const clubSlug = useOrgMemberStore((s) => s.clubSlug);
  const { p } = useOrgPaths();

  const noticesQuery = useQuery({
    queryKey: ['org-consumer-notices', clubSlug],
    enabled: hasClubMemberAccess && Boolean(clubSlug),
    queryFn: async () => {
      const res = await memberPortalService.getNotices(clubSlug!);
      return res.success && res.data ? res.data : [];
    },
  });

  if (!hasClubMemberAccess) return null;

  const noticesPath = p('/notices');
  const recentNotices = (noticesQuery.data ?? []).slice(0, 3);
  const unreadCount = (noticesQuery.data ?? []).filter((n) => !n.isRead).length;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 font-semibold">
            <Megaphone className="h-4 w-4 text-muted-foreground" aria-hidden />
            Notices
            {unreadCount > 0 ? (
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                {unreadCount} new
              </span>
            ) : null}
          </h3>
          {noticesPath ? (
            <Button asChild variant="ghost" size="sm" className="h-auto px-2 py-1 text-xs">
              <Link to={noticesPath}>View all</Link>
            </Button>
          ) : null}
        </div>
        {noticesQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : recentNotices.length > 0 ? (
          <ul className="space-y-3">
            {recentNotices.map((notice) => (
              <li key={notice.id} className="text-sm">
                <p className={cn('font-medium', !notice.isRead && 'text-foreground')}>
                  {notice.title}
                </p>
                <p className="line-clamp-2 text-muted-foreground">{notice.body}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No notices yet</p>
        )}
      </CardContent>
    </Card>
  );
}
